if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        mediaButtons: this.querySelectorAll('[id^="MediaButtons"] [data-target]'),
        prevButton: this.querySelectorAll('button.gallery-button[name="previous-slide"]'),
        nextButton: this.querySelectorAll('button.gallery-button[name="next-slide"]'),
        slider: this.querySelector('[id^="Slider-Thumbnails"]'),
        mediaContainer: this.querySelector('.media-container'),
        dimensionsButton: this.querySelector('.dimensions-button'),
        threeDimensionButton: this.querySelector('.three-dimensions'),
      };

      this.isInModal = false;

      // Handles 'modal-thumbnails-ready' from product-modals.js event to initialize modal elements and trigger a refresh.
      document.addEventListener('modal-thumbnails-ready', event => {
        if (event.detail.thumbnails) {
          this.elements.thumbnails = event.detail.thumbnails;
        }

        if (event.detail.slider) {
          this.elements.slider = event.detail.slider;
        }

        if (event.detail.viewer) {
          this.elements.viewer = event.detail.viewer;
        }

        if (event.detail.prevButton) {
          this.elements.prevButton = event.detail.prevButton;
        }

        if (event.detail.nextButton) {
          this.elements.nextButton = event.detail.nextButton;
        }

        this.isInModal = true;

        this.refresh();

        const variantId = this.getInitialVariantId();
        if (variantId) {
          this.applyVariantFilter(variantId, {
            viewer: this.elements.viewer,
            thumbnails: this.elements.thumbnails,
          });
        }
      });

      // Handles 'modal-closed' from product-modals.js to reset gallery elements from the main page and trigger a refresh.
      document.addEventListener('modal-closed', () => {
        this.elements.thumbnails = document.querySelector(
          '[id^="GalleryThumbnails"]:not([id^="ProductModal"])'
        );
        this.elements.slider = document.querySelector(
          '[id^="Slider-Thumbnails"]:not([id^="ProductModal"])'
        );
        this.elements.viewer = document.querySelector(
          '[id^="GalleryViewer"]:not([id^="ProductModal"])'
        );

        this.isInModal = false;

        this.refresh();
      });

      this.mql = window.matchMedia('(min-width: 750px)');

      this.slideScrollPosition = 0;
      if (!this.elements.viewer || !this.elements.liveRegion) return;

      let currentIndex = 0;
      const slides = Array.from(this.elements.viewer.querySelectorAll('.product-media-item'));
      const totalSlides = slides.length;
      const initialCounter =
        this.elements.viewer?.querySelector('.slider-counter') ||
        this.querySelector('.slider-counter');
      if (initialCounter) {
        initialCounter.innerText = `${currentIndex + 1} / ${totalSlides}`;
      }

      // Set initial thumbnail max-height
      this.setThumbnailMaxHeight();

      window.addEventListener('resize', this.setThumbnailMaxHeight.bind(this));

      // Add media buttons event listener
      this.elements.mediaButtons.forEach(mediaToSwitch => {
        mediaToSwitch.addEventListener(
          'click',
          this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
        );
      });

      this.elements.viewer.addEventListener(
        'slideChanged',
        debounce(this.onSlideChanged.bind(this), 500)
      );
      this.elements.thumbnails?.querySelectorAll('[data-target]')?.forEach(mediaToSwitch => {
        mediaToSwitch
          .querySelector('button')
          ?.addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
      });

      if (this.elements.viewer.querySelectorAll('.product-media-item').length > 2) {
        this.elements.prevButton.forEach(button => {
          button.addEventListener('click', this.onButtonClick.bind(this));
        });

        this.elements.nextButton.forEach(button => {
          button.style.display = 'flex';
          button.addEventListener('click', this.onButtonClick.bind(this));
        });
      }

      this.elements.viewer.querySelector('button[name="previous-slide"]').style.display = 'none';

      this.elements.viewer
        .querySelector('ul')
        .addEventListener('scroll', this.onThumbnailScroll.bind(this));

      this.elements.dimensionsButton?.addEventListener('click', () => {
        this.onDimensionsButtonClick(() => {
          const target = this.elements.dimensionsButton?.dataset?.target;
          if (target) {
            const targetThumb = this.elements.thumbnails?.querySelector(
              `[data-target="${target}"]`
            );
            if (targetThumb) return targetThumb;
          }

          const activeButton = this.elements.thumbnails?.querySelector(
            'button[aria-current="true"]'
          );
          return activeButton?.closest('li') || null;
        });
      });

      this.elements.threeDimensionButton?.addEventListener('click', () => {
        this.onDimensionsButtonClick(() => {
          const target = this.elements.threeDimensionButton?.dataset?.target;
          if (target) {
            const targetThumb = this.elements.thumbnails?.querySelector(
              `[data-target="${target}"]`
            );
            if (targetThumb) return targetThumb;
          }

          return this.elements.thumbnails?.querySelector('.model-thumbnail') || null;
        });
      });

      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches)
        this.removeListSemantic();

      const firstThumbnail = this.elements.thumbnails?.querySelector('li:first-child');
      if (firstThumbnail) {
        this.updateButtonStates(firstThumbnail);
      }

      firstThumbnail?.classList.add('thumbnail-active');

      if (totalSlides === 1) {
        this.elements.viewer.querySelector('button[name="previous-slide"]').style.display = 'none';
      }

      // Listen for variant changes to filter media
      this.bindVariantChangeListener();

      // Apply initial variant filter on page load
      this.applyInitialVariantFilter();
    }

    bindVariantChangeListener() {
      // Listen for PUB_SUB variant change events (event name is 'variant-change')
      if (typeof subscribe !== 'undefined') {
        subscribe('variant-change', this.onVariantChangeFilter.bind(this));
      }
    }

    /**
     * Get the current variant ID on initial page load
     * @returns {string|null}
     */
    getInitialVariantId() {
      const root = this.closest('section') || this.closest('product-info') || document;
      const selectedVariantEl = root.querySelector('variant-selects [data-selected-variant]');

      if (selectedVariantEl?.textContent) {
        try {
          const parsed = JSON.parse(selectedVariantEl.textContent);
          return parsed?.id ? String(parsed.id) : null;
        } catch (error) {
          return null;
        }
      }

      const variantInput = root.querySelector('input[name="id"]');
      return variantInput?.value ? String(variantInput.value) : null;
    }

    /**
     * Apply variant filtering on initial page load
     */
    applyInitialVariantFilter() {
      const variantId = this.getInitialVariantId();
      if (!variantId) return;

      this.onVariantChangeFilter({ data: { variant: { id: variantId } } });
    }

    getSizedImageUrl(url, width) {
      if (!url) return null;
      try {
        const parsed = new URL(url, window.location.origin);
        parsed.searchParams.set('width', width);
        return parsed.toString();
      } catch (error) {
        if (url.includes('width=')) {
          return url.replace(/width=\d+/, `width=${width}`);
        }
        const joiner = url.includes('?') ? '&' : '?';
        return `${url}${joiner}width=${width}`;
      }
    }

    preloadVariantImages(variantId, { width = 500 } = {}) {
      if (!variantId) return;
      if (!this.preloadedImageUrls) {
        this.preloadedImageUrls = new Set();
      }

      const slides = Array.from(this.elements.viewer.querySelectorAll('.product-media-item'));
      slides.forEach(slide => {
        const variantIds = slide.dataset.variantIds || '';
        const matchesVariant = variantIds
          .split(',')
          .map(id => id.trim())
          .filter(Boolean)
          .includes(String(variantId));
        if (!matchesVariant) return;

        const img = slide.querySelector('img');
        const src = img?.currentSrc || img?.src;
        const sizedUrl = this.getSizedImageUrl(src, width);
        if (!sizedUrl || this.preloadedImageUrls.has(sizedUrl)) return;

        const preloadImage = new Image();
        preloadImage.src = sizedUrl;
        this.preloadedImageUrls.add(sizedUrl);
      });
    }

    /**
     * Check if variant filtering is currently active
     * @returns {boolean}
     */
    isVariantFiltered(viewer = this.elements.viewer) {
      const mediaList =
        viewer?.querySelector('.product-media-list') || this.querySelector('.product-media-list');
      return mediaList?.classList.contains('variant-filtered');
    }

    /**
     * Get visible slides (respects variant filtering)
     * @returns {Array<Element>}
     */
    getVisibleSlides(viewer = this.elements.viewer) {
      const selector = this.isVariantFiltered(viewer)
        ? '.product-media-item.for-variant'
        : '.product-media-item';
      return Array.from((viewer || this).querySelectorAll(selector));
    }

    /**
     * Get visible thumbnails (respects variant filtering)
     * @returns {Array<Element>}
     */
    getVisibleThumbnails(thumbnails = this.elements.thumbnails, viewer = this.elements.viewer) {
      const selector = this.isVariantFiltered(viewer)
        ? '.thumbnail-list-item.for-variant'
        : '.thumbnail-list-item';
      return Array.from(thumbnails?.querySelectorAll(selector) || []);
    }

    applyVariantFilter(
      variantId,
      { viewer = this.elements.viewer, thumbnails = this.elements.thumbnails } = {}
    ) {
      if (!viewer) return;
      const variantIdStr = String(variantId);
      const hasVariantId = variantIds => {
        if (!variantIds) return false;
        return variantIds
          .split(',')
          .map(id => id.trim())
          .filter(Boolean)
          .includes(variantIdStr);
      };

      const mediaList = viewer.querySelector('.product-media-list');
      const thumbnailList = thumbnails?.querySelector('.thumbnail-list');
      const slides = Array.from(viewer.querySelectorAll('.product-media-item'));
      const thumbnailItems = thumbnails
        ? Array.from(thumbnails.querySelectorAll('.thumbnail-list-item'))
        : [];

      let mediaCount = 0;
      let hasVariantMedia = false;

      slides.forEach(slide => {
        if (slide.dataset.variantIds && slide.dataset.variantIds.trim() !== '') {
          hasVariantMedia = true;
        }
      });

      if (!hasVariantMedia) {
        mediaList?.classList.remove('variant-filtered');
        thumbnailList?.classList.remove('variant-filtered');
        return;
      }

      mediaList?.classList.add('variant-filtered');
      thumbnailList?.classList.add('variant-filtered');

      slides.forEach(slide => {
        slide.classList.remove('for-variant');
        const variantIds = slide.dataset.variantIds || '';
        if (hasVariantId(variantIds)) {
          slide.classList.add('for-variant');
          mediaCount++;
        }
      });

      thumbnailItems.forEach(thumb => {
        thumb.classList.remove('for-variant');
        const variantIds = thumb.dataset.variantIds || '';
        if (hasVariantId(variantIds)) {
          thumb.classList.add('for-variant');
        }
      });

      if (mediaCount === 0) {
        mediaList?.classList.remove('variant-filtered');
        thumbnailList?.classList.remove('variant-filtered');
        return;
      }

      const firstForVariant = viewer.querySelector('.product-media-item.for-variant');
      if (firstForVariant) {
        this.setActiveMedia(firstForVariant.dataset.mediaId, true);
      }

      this.updateSlideCounter();

      const visibleThumbnails = this.getVisibleThumbnails(thumbnails, viewer);
      if (visibleThumbnails.length > 0) {
        this.updateButtonStates(visibleThumbnails[0]);
      }
    }

    onVariantChangeFilter(eventData) {
      const variantId =
        eventData?.data?.variant?.id || eventData?.variant?.id || eventData?.detail?.id;
      if (!variantId || this.isInModal) return;
      this.applyVariantFilter(variantId);
      this.preloadVariantImages(variantId, { width: 750 });
    }

    onDimensionsButtonClick(getActiveThumbnail) {
      if (!this.elements.thumbnails || !this.elements.slider) return;
      const oldActive = this.elements.thumbnails?.querySelector('.thumbnail-active');
      if (oldActive) {
        oldActive.classList.remove('thumbnail-active');
      }

      const activeThumbnail = getActiveThumbnail();
      if (!activeThumbnail) return;
      const thumbnailOffsetTop = activeThumbnail.offsetTop;
      activeThumbnail.classList.add('thumbnail-active');
      this.setSlidePosition(thumbnailOffsetTop);
      this.updateButtonStates(activeThumbnail);
    }

    updateSlideCounter() {
      // Use only visible slides when variant filtering is active
      const visibleSlides = this.getVisibleSlides();
      const activeThumbnail = this.elements.viewer.querySelector('.product-media-item.is-active');

      if (activeThumbnail) {
        // Find index within visible slides only
        const activeIndex = visibleSlides.indexOf(activeThumbnail) + 1;
        const totalSlides = visibleSlides.length;

        const counterElement = this.elements.viewer.querySelector('.slider-counter');
        if (counterElement) {
          counterElement.innerText = `${activeIndex} / ${totalSlides}`;
        }

        if (activeIndex === 1) {
          this.elements.viewer.querySelector('button[name="previous-slide"]').style.display =
            'none';
        } else {
          this.elements.viewer.querySelector('button[name="previous-slide"]').style.display =
            'flex';
        }

        if (activeIndex === totalSlides) {
          this.elements.viewer.querySelector('button[name="next-slide"]').style.display = 'none';
        } else {
          this.elements.viewer.querySelector('button[name="next-slide"]').style.display = 'flex';
        }
      }
    }

    onThumbnailScroll() {
      // Use only visible slides when variant filtering is active
      const slides = this.getVisibleSlides();
      let closestSlide = null;
      let minDistance = Infinity;

      slides.forEach(slide => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left);

        if (distance < minDistance) {
          minDistance = distance;
          closestSlide = slide;
        }
      });

      if (closestSlide) {
        // Remove is-active from all slides (including hidden ones)
        this.elements.viewer
          .querySelectorAll('.product-media-item')
          .forEach(slide => slide.classList.remove('is-active'));
        closestSlide.classList.add('is-active');

        this.updateSlideCounter();

        const mediaId = closestSlide.dataset.mediaId;

        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);

        this.playActiveMedia(closestSlide);
      }
    }

    setThumbnailMaxHeight(event) {
      if (!this.elements.thumbnails || !this.elements.viewer) return;
      const thumbnailsList = this.elements.thumbnails.querySelector('.thumbnail-list');
      const mediasList = this.elements.viewer.querySelector('.product-media-list');

      if (!thumbnailsList) return;

      if (!this.isInModal) {
        let galleryViewerHeight = this.elements.viewer.offsetHeight * 0.6;

        if (event) {
          galleryViewerHeight = this.elements.viewer.offsetHeight - 60;
        }

        thumbnailsList.style.maxHeight = `${galleryViewerHeight}px`;

        const thumbnailHeight = galleryViewerHeight / 6;

        this.elements.thumbnails.querySelectorAll('.thumbnail-list-item').forEach(element => {
          element.style.height = `${thumbnailHeight}px`;
          element.style.width = '';
        });
      } else {
        const thumbnailWidth = 80;
        const gap = 10;
        const visible = 6;

        const totalWidth = thumbnailWidth * visible + gap * (visible - 1);

        this.mql = window.matchMedia('(min-width: 750px)');

        thumbnailsList.style.maxWidth = `${totalWidth}px`;

        if (this.mql.matches) {
          mediasList.style.minWidth = `${totalWidth}px`;
          mediasList.style.maxWidth = `${totalWidth}px`;
          mediasList.style.minHeight = `400px`;
        }
      }
    }

    onSlideChanged(event) {
      if (!this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${event.detail.currentElement?.dataset.mediaId}"]`
      );
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia =
        this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
        this.elements.viewer.querySelector('[data-media-id]');

      if (!activeMedia) {
        return;
      }

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach(el => {
        el.classList.remove('is-active');
      });

      if (activeMedia.querySelector('product-model')) {
        activeMedia.querySelector('.product-media').style.display = 'none';
      }

      activeMedia.classList.add('is-active');

      if (prepend) {
        if (activeMedia.parentElement.firstChild !== activeMedia) {
          activeMedia.parentElement.prepend(activeMedia);
        }

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );
          if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }
      }

      this.preventStickyHeader();

      const timeoutDelay = this.mql.matches ? 50 : 10;

      window.setTimeout(() => {
        if (!this.mql.matches || this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: this.mql.matches ? 'smooth' : 'auto',
          });
        }
      }, timeoutDelay);

      this.playActiveMedia(activeMedia);

      const activeThumbnail = this.elements.thumbnails?.querySelector?.(
        `[data-target="${mediaId}"]`
      );

      if (activeThumbnail && this.elements.thumbnails) {
        const viewerMediaList = this.elements.viewer.querySelector('.product-media-list');
        const isVariantFiltered = viewerMediaList?.classList?.contains('variant-filtered');
        if (isVariantFiltered) {
          activeMedia.classList.add('for-variant');
          activeThumbnail.classList.add('for-variant');
        }

        this.elements.thumbnails.querySelectorAll('.thumbnail-active').forEach(thumb => {
          thumb.classList.remove('thumbnail-active');
          const btn = thumb.querySelector('button');
          if (btn) btn.removeAttribute('aria-current');
        });

        activeThumbnail.classList.add('thumbnail-active');
        const button = activeThumbnail.querySelector('button');
        if (button) {
          button.setAttribute('aria-current', 'true');
        }

        this.updateButtonStates(activeThumbnail);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      } else {
        this.announceLiveRegion(activeMedia, activeMedia.dataset.index);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails
        .querySelectorAll('button')
        .forEach(element => element.removeAttribute('aria-current'));

      const oldActive = this.elements.thumbnails.querySelector('.thumbnail-active');
      if (oldActive) {
        oldActive.classList.remove('thumbnail-active');
      }

      thumbnail.classList.add('thumbnail-active');
      const button = thumbnail.querySelector('button');
      if (button) {
        button.setAttribute('aria-current', 'true');
      }

      if (this.isInModal) {
        const container = this.elements.slider;
        const scrollLeft =
          thumbnail.offsetLeft - container.clientWidth / 2 + thumbnail.clientWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      } else {
        this.elements.slider.scrollTo({
          top: thumbnail.offsetTop,
          behavior: 'smooth',
        });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    onButtonClick(event) {
      const isMobile = window.innerWidth <= 1050;
      const isNext = event.currentTarget.name === 'next-slide';

      if (this.isInModal || isMobile) {
        const currentActiveSlide = this.elements.viewer.querySelector(
          '.product-media-item.is-active'
        );

        // Get visible slides and find current position
        const visibleSlides = this.getVisibleSlides();
        const currentIndex = visibleSlides.indexOf(currentActiveSlide);
        const newIndex = isNext ? currentIndex + 1 : currentIndex - 1;
        const newActiveSlide = visibleSlides[newIndex];

        if (newActiveSlide) {
          this.elements.viewer
            .querySelectorAll('.product-media-item.is-active')
            .forEach(el => el.classList.remove('is-active'));
          newActiveSlide.classList.add('is-active');

          this.updateSlideCounter();

          const mediaId = newActiveSlide.dataset.mediaId;
          this.setActiveMedia(mediaId, false);

          const mediaList =
            this.elements.viewer.querySelector('.product-media-list') || this.elements.viewer;
          mediaList.scrollTo({
            left: newActiveSlide.offsetLeft,
            behavior: 'smooth',
          });
        }
      } else {
        const currentActiveThumbnailBtn = this.elements.thumbnails.querySelector(
          'button[aria-current="true"]'
        );
        const currentLi = currentActiveThumbnailBtn?.closest('li');

        // Get visible thumbnails and find current position
        const visibleThumbnails = this.getVisibleThumbnails();
        const currentIdx = visibleThumbnails.indexOf(currentLi);
        const newIdx = isNext ? currentIdx + 1 : currentIdx - 1;
        const newActiveThumbnail = visibleThumbnails[newIdx];

        if (newActiveThumbnail) {
          const mediaId = newActiveThumbnail.dataset.target;

          this.setActiveMedia(mediaId, false);

          const thumbnailOffsetTop = newActiveThumbnail.offsetTop;
          this.setSlidePosition(thumbnailOffsetTop);
          this.updateButtonStates(newActiveThumbnail);
        }
      }
    }

    updateButtonStates(newActiveThumbnail) {
      if (!this.elements.thumbnails) return;
      const nextButton = this.elements.thumbnails.querySelector('button[name="next-slide"]');
      const prevButton = this.elements.thumbnails.querySelector('button[name="previous-slide"]');

      // Use visible thumbnails to determine first/last position
      const visibleThumbnails = this.getVisibleThumbnails();
      const firstThumbnail = visibleThumbnails[0];
      const lastThumbnail = visibleThumbnails[visibleThumbnails.length - 1];

      if (prevButton) prevButton.disabled = newActiveThumbnail === firstThumbnail;
      if (nextButton) nextButton.disabled = newActiveThumbnail === lastThumbnail;
    }

    setSlidePosition(position) {
      if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);

      const slider = this.elements.slider;
      if (!slider) return;

      const maxScroll = this.isInModal
        ? slider.scrollWidth - slider.clientWidth
        : slider.scrollHeight - slider.clientHeight;

      const boundedPosition = Math.max(0, Math.min(position, maxScroll));

      this.setPositionTimeout = setTimeout(() => {
        slider.scrollTo({
          [this.isInModal ? 'left' : 'top']: boundedPosition,
          behavior: 'smooth',
        });
      }, 100);
    }

    // Refreshes gallery state: when opening or close modal
    refresh() {
      this.setThumbnailMaxHeight();
      if (!this.elements.thumbnails) {
        this.updateSlideCounter();
        return;
      }

      const currentActiveButton = this.elements.thumbnails?.querySelector(
        'button[aria-current="true"]'
      );
      if (currentActiveButton) {
        const activeMediaId = currentActiveButton.closest('li')?.dataset?.target;
        if (activeMediaId) {
          const modalActiveThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${activeMediaId}"]`
          );
          if (modalActiveThumbnail) {
            modalActiveThumbnail.classList.add('thumbnail-active');
            this.setActiveThumbnail(modalActiveThumbnail);
            this.setActiveMedia(activeMediaId, false);
          }
        }
      } else {
        const fallbackThumbnail = this.elements.thumbnails.querySelector('li:first-child');
        if (fallbackThumbnail) {
          fallbackThumbnail.classList.add('thumbnail-active');
          this.setActiveThumbnail(fallbackThumbnail);
          this.setActiveMedia(fallbackThumbnail.dataset.target, false);
        }
      }

      if (this.elements.prevButton?.forEach && this.elements.nextButton?.forEach) {
        if (!this.onButtonClickBound) {
          this.onButtonClickBound = this.onButtonClick.bind(this);
        }

        this.elements.prevButton.forEach(button => {
          button.removeEventListener('click', this.onButtonClickBound);
          button.addEventListener('click', this.onButtonClickBound);
        });

        this.elements.nextButton.forEach(button => {
          button.removeEventListener('click', this.onButtonClickBound);
          button.addEventListener('click', this.onButtonClickBound);
        });
      }

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach(mediaToSwitch => {
        const button = mediaToSwitch.querySelector('button');
        if (!button.dataset.listenerSet) {
          button.addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
          button.dataset.listenerSet = 'true';
        }
      });

      const viewerList = this.elements.viewer.querySelector('ul');
      if (viewerList && !viewerList.dataset.listenerSet) {
        viewerList.addEventListener('scroll', this.onThumbnailScroll.bind(this));
        viewerList.dataset.listenerSet = 'true';
      }

      this.updateSlideCounter();
    }
  }

  customElements.define('media-gallery', MediaGallery);

  customElements.define(
    'variant-media-gallery',
    class VariantMediaGallery extends MediaGallery {
      connectedCallback() {
        this.bindListeners();
      }

      bindListeners() {
        const variantRadios = document.getElementById(`VariantRadios-${this.dataset.sectionId}`);
        if (variantRadios) {
          variantRadios.addEventListener('variantChange', this.onVariantChange.bind(this));
        }
      }

      /**
       * when variant changes, iterate over slides
       * if the variantId data property contains the current variant ID, then show
       *
       * after, update media count for a11y
       */
      onVariantChange(e) {
        const variantId = e.detail.id;

        if (variantId) {
          const slides = Array.from(this.querySelectorAll('.product-media-item'));

          let mediaCount = 0;

          slides.forEach(slide => {
            slide.classList.remove('for-variant');
            if (
              typeof slide.dataset.variantIds !== 'undefined' &&
              slide.dataset.variantIds !== null &&
              slide.dataset.variantIds.includes(variantId)
            ) {
              slide.classList.add('for-variant');
              mediaCount++;
            }
          });
          const dimensionButtons = this.querySelectorAll('.dimensions-button');

          dimensionButtons.forEach(button => {
            const targetMediaId = button.dataset.target;
            const targetSlide = this.querySelector(
              `.product-media-item[data-media-id="${targetMediaId}"]`
            );

            if (
              targetSlide &&
              typeof targetSlide.dataset.variantIds !== 'undefined' &&
              targetSlide.dataset.variantIds.includes(variantId)
            ) {
              button.style.display = 'inline-flex';
            } else {
              button.style.display = 'none';
            }
          });

          setTimeout(() => {
            this.querySelector('.slider-counter-total').textContent = mediaCount;
          });
        }
      }
    }
  );
}
